import type { ReactNode } from "react";
import { RouteBodyStateProvider } from "#route-body-state";
import { useHiddenBodyRetention } from "#use-hidden-body-retention";

/**
 * The heavy part of a route (list rows, native views, query observers), mounted while `isVisible`
 * and for a grace period after it is hidden, then released.
 *
 * Keep light UI state (filters, sort, open sections, the query) in the parent so it survives the
 * release, and persist scroll positions through route-body state (e.g. `HeaderLegendList`'s
 * `scrollPosition`): the snapshot here sits above the boundary, so a rebuilt list reopens where the
 * learner left it. Nothing but those scalars is retained while released.
 */
export function ReleasableRouteBody({
  children,
  fallback = null,
  isVisible,
}: {
  children: ReactNode;
  /** Shown only if the hidden route is displayed before its visibility updates. */
  fallback?: ReactNode;
  isVisible: boolean;
}) {
  const isRetained = useHiddenBodyRetention(isVisible);
  return <RouteBodyStateProvider>{isRetained ? children : fallback}</RouteBodyStateProvider>;
}
